/** Extract image URLs from WordPress post HTML (content/excerpt). */
export function extractImageUrlsFromHtml(html: string): string[] {
  if (!html) return [];

  const urls: string[] = [];
  const imgTagRegex = /<img[^>]*>/gi;
  let tagMatch: RegExpExecArray | null;

  while ((tagMatch = imgTagRegex.exec(html)) !== null) {
    const tag = tagMatch[0];
    const srcMatch =
      tag.match(/\ssrc=["']([^"']+)["']/i) ||
      tag.match(/\sdata-src=["']([^"']+)["']/i);
    const url = srcMatch?.[1]?.trim();
    if (url && !urls.includes(url)) {
      urls.push(url);
    }
  }

  return urls;
}

/**
 * Primary image for cards/hero: WP featured image, else generated image in body
 * (often the 2nd <img> from the blog poster), else first in-body image.
 */
export function resolveBlogPostImageUrl(post: {
  featuredMediaUrl?: string;
  contentHtml?: string;
  excerptHtml?: string;
}): string {
  const featured = post.featuredMediaUrl?.trim();
  if (featured) return featured;

  const contentImages = extractImageUrlsFromHtml(post.contentHtml || '');
  if (contentImages.length >= 2) return contentImages[1];
  if (contentImages.length >= 1) return contentImages[0];

  const excerptImages = extractImageUrlsFromHtml(post.excerptHtml || '');
  if (excerptImages.length >= 1) return excerptImages[0];

  return '';
}

export interface BlogPost {
  id: number;
  title: string;          // decoded text (UI ready)
  titleHtml: string;      // raw HTML (optional if needed)
  contentHtml: string;    // full HTML content
  excerptHtml: string;    // summary HTML
  slug: string;
  link: string;
  date: Date;
  modified: Date;
  authorId: number;
  authorName?: string;
  authorAvatar?: string;
  featuredMediaId: number;
  featuredMediaUrl?: string;
  categories: number[];
  categoryNames?: string[];
  tags: number[];
  status: string;
  format: string;
  isSticky: boolean;
  commentStatus: 'open' | 'closed';
}

export function mapWPPost(post: any): BlogPost {
  const embedded = post._embedded || {};
  
  // Extract featured image
  const featuredMedia = embedded['wp:featuredmedia']?.[0];
  const featuredFromEmbed = featuredMedia?.source_url || '';
  const contentHtml = post.content?.rendered || '';
  const excerptHtml = post.excerpt?.rendered || '';

  const featuredMediaUrl = resolveBlogPostImageUrl({
    featuredMediaUrl: featuredFromEmbed,
    contentHtml,
    excerptHtml,
  });

  // Extract author
  const author = embedded['author']?.[0];
  const authorName = author?.name || 'Baoafrik Team';
  const authorAvatar = author?.avatar_urls?.['96'] || author?.avatar_urls?.['48'] || '';

  // Extract category names
  const categoryTerms = (embedded['wp:term']?.[0] || []).filter((term: any) => term.taxonomy === 'category');
  const categoryNames = categoryTerms.map((term: any) => term.name);

  return {
    id: post.id,

    // Title
    title: decodeHtml(post.title?.rendered || ''),
    titleHtml: post.title?.rendered || '',

    // Content
    contentHtml,
    excerptHtml,

    // Routing
    slug: post.slug,
    link: post.link,

    // Dates
    date: new Date(post.date),
    modified: new Date(post.modified),

    // Meta
    authorId: post.author,
    authorName,
    authorAvatar,
    
    featuredMediaId: post.featured_media,
    featuredMediaUrl,

    categories: post.categories || [],
    categoryNames,
    tags: post.tags || [],

    status: post.status,
    format: post.format,

    isSticky: post.sticky,
    commentStatus: post.comment_status,
  };
}

function decodeHtml(html: string): string {
  if (typeof window === 'undefined') return html;

  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}